import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StarRating } from "@/components/StarRating";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, Plus, Globe, Lock, Utensils, Wine, Clapperboard } from "lucide-react";
import { MoviePoster } from "@/components/MoviePoster";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

export default function MyPairings() {
  const navigate = useNavigate();
  const [pairings, setPairings] = useState(null);
  const [search, setSearch] = useState("");

  const load = async (q = "") => {
    const { data } = await api.get("/pairings", { params: q ? { search: q } : {} });
    setPairings(data);
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  return (
    <div className="min-h-screen pt-24 pb-20 px-5 sm:px-8" data-testid="my-pairings-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8 animate-fade-up">
          <div>
            <span className="uppercase text-xs tracking-[0.3em] text-[#E50914]">
              Your Collection
            </span>
            <h1 className="font-display text-4xl sm:text-5xl font-bold mt-2">
              My Pairings
            </h1>
          </div>
          <Button
            onClick={() => navigate("/generate")}
            data-testid="pairings-generate-btn"
            className="rounded-full bg-[#E50914] hover:bg-[#c00811] text-white h-11 px-6 self-start"
          >
            <Plus className="w-4 h-4 mr-1" /> Generate New
          </Button>
        </div>

        <div className="relative mb-8 max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="pairings-search-input"
            placeholder="Search by movie title..."
            className="pl-12 h-12 bg-[#0d0d0d] border-white/10 rounded-full"
          />
        </div>

        {pairings === null && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-56 rounded-2xl bg-white/5" />
            ))}
          </div>
        )}

        {pairings && pairings.length === 0 && (
          <div
            className="rounded-2xl border border-white/10 bg-[#0d0d0d] p-16 text-center"
            data-testid="pairings-empty-state"
          >
            <Clapperboard className="w-14 h-14 text-[#E50914] mx-auto mb-5" />
            <h3 className="font-display text-2xl font-bold mb-2">
              {search ? "No pairings match that title." : "No pairings yet."}
            </h3>
            <p className="text-white/50 mb-6">
              {search
                ? "Try a different movie name."
                : "Generate your first movie dinner pairing to get started."}
            </p>
            {!search && (
              <Button
                onClick={() => navigate("/generate")}
                data-testid="empty-generate-btn"
                className="rounded-full bg-[#E50914] hover:bg-[#c00811] text-white h-11 px-8"
              >
                <Plus className="w-4 h-4 mr-1" /> Generate Your First Pairing
              </Button>
            )}
          </div>
        )}

        {pairings && pairings.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {pairings.map((p) => (
              <Link
                key={p.id}
                to={`/pairings/${p.id}`}
                data-testid={`pairing-card-${p.id}`}
                className="group rounded-2xl border border-white/5 bg-[#0d0d0d] overflow-hidden hover:border-white/20 hover:scale-[1.02] transition-all duration-300 flex flex-col"
              >
                <div className="relative">
                  <MoviePoster title={p.movie_title} className="h-44 w-full" />
                  <div className="absolute top-3 right-3">
                    {p.is_public ? (
                      <span className="flex items-center justify-center w-7 h-7 rounded-full bg-black/50 backdrop-blur">
                        <Globe className="w-4 h-4 text-[#5EC5A0]" />
                      </span>
                    ) : (
                      <span className="flex items-center justify-center w-7 h-7 rounded-full bg-black/50 backdrop-blur">
                        <Lock className="w-4 h-4 text-white/60" />
                      </span>
                    )}
                  </div>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <span className="font-display text-lg font-bold leading-tight group-hover:text-[#E50914] transition-colors mb-3">
                    {p.movie_title}
                  </span>
                  <div className="space-y-2 text-sm text-white/70 flex-1">
                    <div className="flex items-center gap-2">
                      <Utensils className="w-4 h-4 text-[#E50914]" />
                      <span className="truncate">{p.meal_name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Wine className="w-4 h-4 text-[#F5B841]" />
                      <span className="truncate">{p.drink_name}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-5 pt-4 border-t border-white/5">
                    <StarRating value={p.rating} readOnly size={15} />
                    <span className="text-xs text-white/40">{formatDate(p.created_at)}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
